import { FileText } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { ImageManager } from './ImageManager';
import { SchedulingControls } from './SchedulingControls';
import type { Attachment } from './types';
import type { Platform, PostStatus } from '../../content-calendar/types';

export interface EditSectionProps {
  text: string;
  onTextChange: (text: string) => void;
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  date: string;
  time: string;
  platform: Platform;
  status: PostStatus;
  pageName?: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  onPlatformChange: (platform: Platform) => void;
  onStatusChange: (status: PostStatus) => void;
  dropZoneRef: React.RefObject<HTMLDivElement | null>;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
  uploadError: string | null;
  setUploadError: (error: string | null) => void;
  isInline?: boolean;
}

export function EditSection({
  text,
  onTextChange,
  attachments,
  onAttachmentsChange,
  date,
  time,
  platform,
  status,
  pageName,
  onDateChange,
  onTimeChange,
  onPlatformChange,
  onStatusChange,
  dropZoneRef,
  isUploading,
  setIsUploading,
  uploadError,
  setUploadError,
  isInline = false,
}: EditSectionProps) {
  return (
    <div className={`flex flex-col overflow-hidden h-full py-2`}>
      <div
        ref={dropZoneRef}
        className={`flex-1 overflow-y-auto flex flex-col rounded-md relative space-y-2`}
      >
        {/* POST CONTENT */}
        <div className={`border border-border rounded-lg bg-card ${isInline ? 'p-3 space-y-3' : 'p-4 space-y-4'}`}>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Content
          </h3>

          {/* Text Editor */}
          <div>
            <label className="text-sm font-medium mb-2 block">Text</label>
            <Textarea
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder="Write your post text..."
              className="min-h-[120px] max-h-[300px] resize-y bg-background"
            />
          </div>

          {/* Image Management */}
          <ImageManager
            attachments={attachments}
            onAttachmentsChange={onAttachmentsChange}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            dropZoneRef={dropZoneRef}
          />
        </div>

        {/* Scheduling Controls */}
        <SchedulingControls
          date={date}
          time={time}
          platform={platform}
          status={status}
          pageName={pageName}
          onDateChange={onDateChange}
          onTimeChange={onTimeChange}
          onPlatformChange={onPlatformChange}
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
}
